import * as React from "react";
import {
  DayPicker,
  type DateRange as DayPickerRange,
  type Matcher,
} from "react-day-picker";
import { format, isValid, parseISO, startOfMonth } from "date-fns";
import { useTranslation } from "react-i18next";
import useOnClickOutside from "../hooks/onClickOutside";
import { twMerge } from "tailwind-merge";
import Button from "./Button";
import { Tools } from "../utils";

export type DatePickerHandle = {
  open: () => void;
  close: () => void;
};

export type DatePickerRangeValue = {
  from?: string | null; // YYYY-MM-DD
  to?: string | null; // YYYY-MM-DD
};

export type DatePickerSingleProps = {
  value?: string | null;
  onChange: (next: string) => void;
};

export type DatePickerRangeProps = {
  value?: DatePickerRangeValue | null;
  onChange: (next: DatePickerRangeValue) => void;
  clear?: boolean;
};

type DatePickerContextValue = {
  open: boolean;
  close: () => void;
  min?: Date;
  max?: Date;
  startMonth?: Date;
  endMonth?: Date;
  disabled: Matcher[];
};

const DatePickerContext = React.createContext<DatePickerContextValue | null>(
  null,
);

function useDatePickerContext() {
  const ctx = React.useContext(DatePickerContext);
  if (!ctx) throw new Error("DatePicker.* must be used within <DatePicker />");
  return ctx;
}

export type DatePickerRootProps = {
  className?: string;
  children: React.ReactNode;
  /** Inclusive first selectable day (`YYYY-MM-DD`). */
  minDate?: string | null;
  /** Inclusive last selectable day (`YYYY-MM-DD`). */
  maxDate?: string | null;
};

export type DatePickerProps = DatePickerRootProps;

function DatePickerSingle({ value, onChange }: DatePickerSingleProps) {
  const { close, startMonth, endMonth, disabled } = useDatePickerContext();

  const selected =
    value && typeof value === "string" && isValid(parseISO(value))
      ? parseISO(value)
      : undefined;

  return (
    <>
      <DayPicker
        mode="single"
        selected={selected}
        defaultMonth={selected}
        startMonth={startMonth}
        endMonth={endMonth}
        disabled={disabled}
        onSelect={(d) => {
          if (!d) return;
          onChange(format(d, "yyyy-MM-dd"));
          close();
        }}
      />
    </>
  );
}

function DatePickerRange({ value, onChange, clear }: DatePickerRangeProps) {
  const { t } = useTranslation();
  const { open, close, startMonth, endMonth, disabled } =
    useDatePickerContext();

  const parsedRange = React.useMemo<DayPickerRange>(() => {
    const from =
      value?.from && isValid(parseISO(value.from))
        ? parseISO(value.from)
        : undefined;
    const to =
      value?.to && isValid(parseISO(value.to)) ? parseISO(value.to) : undefined;
    return { from, to };
  }, [value?.from, value?.to]);

  const [pending, setPending] = React.useState<DayPickerRange | undefined>(
    parsedRange,
  );

  React.useEffect(() => {
    if (!open) return;
    setPending(parsedRange);
  }, [open, parsedRange]);

  return (
    <>
      <DayPicker
        mode="range"
        selected={pending}
        onSelect={(next, selectedDay) => {
          if (!selectedDay) return;
          setPending(
            pending?.from && pending?.to
              ? { from: selectedDay, to: undefined }
              : next,
          );
        }}
        defaultMonth={pending?.from}
        startMonth={startMonth}
        endMonth={endMonth}
        disabled={disabled}
      />
      <div
        className={twMerge(
          "flex items-center gap-2 border-t border-border pt-3 mt-3",
          clear ? "justify-between" : "justify-end",
        )}
      >
        {clear && (
          <Button
            variant="outline"
            color="critical"
            disabled={!value?.from}
            onClick={() => {
              setPending(undefined);
              onChange({ from: "", to: "" });
              close();
            }}
          >
            {t("general.clear")}
          </Button>
        )}
        <div className="flex gap-2">
          <Button variant="outline" color="secondary" onClick={close}>
            {t("general.cancel")}
          </Button>
          <Button
            disabled={!pending?.from}
            color="primary"
            variant="solid"
            onClick={() => {
              if (!pending?.from) return;
              const from = format(pending.from, "yyyy-MM-dd");
              const to = format(pending.to ?? pending.from, "yyyy-MM-dd");
              onChange({ from, to });
              close();
            }}
          >
            {t("general.done")}
          </Button>
        </div>
      </div>
    </>
  );
}

const DatePickerRoot = React.forwardRef<DatePickerHandle, DatePickerProps>(
  function DatePicker(props, ref) {
    const anchorRef = React.useRef<HTMLDivElement | null>(null);
    const { open, setOpen } = useOnClickOutside(anchorRef);
    const close = React.useCallback(() => setOpen(false), [setOpen]);

    const min = Tools.parseYmdToDay(props.minDate);
    const max = Tools.parseYmdToDay(props.maxDate);
    const startMonth = min && startOfMonth(min);
    const endMonth = max && startOfMonth(max);
    const disabled = [
      ...(min ? ([{ before: min }] satisfies Matcher[]) : []),
      ...(max ? ([{ after: max }] satisfies Matcher[]) : []),
    ];

    React.useImperativeHandle(
      ref,
      () => ({ open: () => setOpen(true), close }),
      [setOpen, close],
    );

    return (
      <DatePickerContext.Provider
        value={{
          open,
          close,
          disabled,
          min: min ?? undefined,
          max: max ?? undefined,
          startMonth: startMonth ?? undefined,
          endMonth: endMonth ?? undefined,
        }}
      >
        {open ? (
          <div
            ref={anchorRef}
            role="dialog"
            aria-label="Custom date picker"
            className={twMerge(
              "p-3 absolute left-0 mt-[10px] z-30 rounded-[8px] bg-surface border border-divider shadow-md w-max",
              props.className,
            )}
          >
            {props.children}
          </div>
        ) : null}
      </DatePickerContext.Provider>
    );
  },
);

export const DatePicker = Object.assign(DatePickerRoot, {
  Single: DatePickerSingle,
  Range: DatePickerRange,
});
